import { Alert, Typography } from '@mui/material';
import React from 'react';
import type { RegistryDocument, RegistryState } from '../data/types';
import { investorSummary, quarterStart } from '../domain/investorSummary';
import { date, money, units as formatUnits } from '../lib/format';
import { LEDGER_LABELS } from '../lib/labels';
import DocumentPaper, { DocTable } from './DocumentPaper';

export default function InvestorStatementView({
  state,
  doc,
}: {
  state: RegistryState;
  doc: RegistryDocument;
}) {
  const holder = state.holders.find((item) => item.id === doc.holderId);
  if (!holder) return <Alert severity="info">Statement details are not available.</Alert>;
  const tenant = state.tenants.find((item) => item.id === holder.tenantId) ?? state.tenants[0];
  const run = state.reportRuns.find((item) => item.id === doc.refId);
  const from = quarterStart(doc.date);
  const summaries = investorSummary(state, holder.id, doc.date, from, run?.fundIds).filter(
    (summary) => summary.units > 0 || summary.periodEntries.length > 0,
  );
  const nameOf = (fundId: string, classId: string) =>
    `${state.funds.find((fund) => fund.id === fundId)?.name} · ${state.classes.find((item) => item.id === classId)?.name}`;
  const entries = summaries
    .flatMap((summary) => summary.periodEntries)
    .sort((left, right) => left.effectiveDate.localeCompare(right.effectiveDate));
  return (
    <DocumentPaper
      tenant={tenant}
      holder={holder}
      issued={doc.date}
      title="Investor statement"
      subtitle={`${date(from)} to ${date(doc.date)}`}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Your investment at a glance
      </Typography>
      <DocTable
        head={['Trust and class', 'Invested', 'Income received', 'Capital returned', 'Value now']}
        rows={summaries.map((summary) => [
          nameOf(summary.fundId, summary.classId),
          money(summary.invested),
          money(summary.incomeReceived),
          money(summary.capitalReturned),
          `${money(summary.value)} (${formatUnits(summary.units)} units)`,
        ])}
      />
      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        Transactions this quarter
      </Typography>
      {entries.length ? (
        <DocTable
          head={['Date', 'Transaction', 'Units', 'Amount']}
          rows={entries.map((entry) => [
            date(entry.effectiveDate),
            `${LEDGER_LABELS[entry.type]} · ${state.funds.find((fund) => fund.id === entry.fundId)?.code}`,
            entry.units ? formatUnits(entry.units) : '—',
            money(entry.amount, true),
          ])}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No transactions this quarter.
        </Typography>
      )}
    </DocumentPaper>
  );
}
